/* eslint-disable max-lines */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { flexRender, RowSelectionState } from '@tanstack/react-table';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import cn from 'classnames';
import { useThemeContext } from '../../../utils/providers/theme-context';
import { DataGridColumn } from './data-grid-types';
import { GridAction } from './grid-action-controller';
import { RowActionsCell } from './row-action-v2';
import Checkbox from '../ui-kit/checkbox';
import { ColumnFiltersState } from '@tanstack/react-table';

/* -------------------- BulkActionsBar -------------------- */
interface BulkActionsBarProps<T> {
  selected: T[];
  getBulkActions: (rows: T[]) => Array<{
    id: string;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
    destructive?: boolean;
  }>;
  onExecuteBulk: (rows: T[], actionId: string) => void;
  themeLayer: string;
  themeBorder: string;
}

export function BulkActionsBar<T>({
  selected,
  getBulkActions,
  onExecuteBulk,
  themeLayer,
  themeBorder
}: BulkActionsBarProps<T>) {
  const actions = getBulkActions(selected);
  if (!selected.length || !actions.length) return null;

  return (
    <div className="sticky bottom-2 left-0 right-0 mx-2 sm:mx-0 z-40">
      <div
        className="flex flex-wrap items-center gap-2 rounded-2xl px-3 py-2 shadow-card backdrop-blur-sm"
        style={{
          backgroundColor: themeLayer,
          border: `1px solid ${themeBorder}`
        }}
      >
        <span className="text-xs opacity-70">{selected.length} selected</span>
        <div className="h-4 w-px bg-zinc-300/70 dark:bg-zinc-700/70" />
        {actions.map(a => (
          <button
            key={a.id}
            type="button"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs ring-1 transition',
              a.destructive
                ? 'ring-red-300 text-red-700 dark:ring-red-700/60 dark:text-red-300 hover:bg-red-50/60 dark:hover:bg-red-900/20'
                : 'ring-zinc-300 text-zinc-800 dark:ring-zinc-700/60 dark:text-zinc-100 hover:bg-zinc-50/60 dark:hover:bg-zinc-800/40',
              a.disabled && 'opacity-60 cursor-not-allowed'
            )}
            disabled={a.disabled}
            onClick={() => !a.disabled && onExecuteBulk(selected, a.id)}
          >
            {a.icon && <span className="shrink-0">{a.icon}</span>}
            <span className="truncate">{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* -------------------- MobileFilterModal -------------------- */
interface MobileFilterModalProps<T> {
  columns: DataGridColumn<T>[];
  enableFiltering: boolean;
  columnFilters: ColumnFiltersState;
  commitFilterValue: (
    columnId: string,
    value: string,
    isCommit: boolean
  ) => void;
  themeText: string;
  themeLayer: string;
  themeBorder: string;
}

export function MobileFilterModal<T>({
  columns,
  enableFiltering,
  columnFilters,
  commitFilterValue,
  themeText,
  themeLayer,
  themeBorder
}: MobileFilterModalProps<T>) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  if (!enableFiltering) return null;

  const filterableColumns = columns.filter(
    col =>
      col.enableFiltering !== false &&
      col.id !== 'select' &&
      col.id !== 'expand' &&
      col.id !== 'actions'
  );

  if (filterableColumns.length === 0) return null;

  const hasActiveFilter = columnFilters.length > 0;
  const [selectedColumn, setSelectedColumn] = useState<string>('');
  const [localValue, setLocalValue] = useState<string>('');

  React.useEffect(() => {
    if (selectedColumn) {
      const currentFilter = columnFilters.find(f => f.id === selectedColumn);
      setLocalValue((currentFilter?.value as string) || '');
    } else {
      setLocalValue('');
    }
  }, [selectedColumn, columnFilters]);

  const handleApply = () => {
    if (selectedColumn && localValue)
      commitFilterValue(selectedColumn, localValue, true);
    setIsOpen(false);
  };

  const handleClearSelected = () => {
    if (selectedColumn) {
      commitFilterValue(selectedColumn, '', true);
      setSelectedColumn('');
      setLocalValue('');
    }
  };

  const handleClearAll = () => {
    columnFilters.forEach(filter => commitFilterValue(filter.id, '', true));
    setSelectedColumn('');
    setLocalValue('');
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-12 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg transition-transform active:scale-95"
        style={{ backgroundColor: getColour('activeText'), color: 'white' }}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        <span className="font-medium text-sm">{t('filter')}</span>
        {hasActiveFilter && (
          <span className="px-1.5 py-0.5 text-xs rounded-full bg-white/30 dark:bg-zinc-700/50">
            {columnFilters.length}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.2 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black z-50"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl shadow-2xl max-h-[70vh] overflow-hidden mx-2 mb-2"
              style={{ backgroundColor: themeLayer }}
            >
              <div
                className="flex items-center justify-between px-4 py-3 border-b"
                style={{ borderColor: themeBorder }}
              >
                <h3
                  className="text-sm font-semibold"
                  style={{ color: themeText }}
                >
                  {t('filter')}
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-full hover:bg-opacity-10 transition-colors"
                  style={{ backgroundColor: `${themeText}10` }}
                >
                  <X className="w-4 h-4" style={{ color: themeText }} />
                </button>
              </div>

              <div className="px-4 py-4 space-y-4 overflow-y-auto max-h-[55vh]">
                <div>
                  <label
                    className="block text-xs font-medium mb-2"
                    style={{ color: themeText }}
                  >
                    {t('filter-by-column') || 'Filter by column'}
                  </label>
                  <div className="space-y-2">
                    {filterableColumns.map(col => {
                      const colId = (col.id ||
                        col.field ||
                        col.accessorKey) as string;
                      const label =
                        typeof col.header === 'string' ? col.header : colId;
                      const isChecked = selectedColumn === colId;

                      return (
                        <label
                          key={colId}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="filter-column"
                            value={colId}
                            checked={isChecked}
                            onChange={() => {
                              setSelectedColumn(colId);
                              const existingFilter = columnFilters.find(
                                f => f.id === colId
                              );
                              setLocalValue(
                                (existingFilter?.value as string) || ''
                              );
                            }}
                            className="w-4 h-4 rounded-full border transition cursor-pointer"
                            style={{ accentColor: getColour('activeText') }}
                          />
                          <span
                            className="text-sm"
                            style={{ color: themeText }}
                          >
                            {label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {selectedColumn &&
                  (() => {
                    const column = filterableColumns.find(
                      c => (c.id || c.field || c.accessorKey) === selectedColumn
                    );
                    if (!column) return null;

                    const filterVariant = column.filterVariant || 'text';
                    const inputType =
                      filterVariant === 'number'
                        ? 'number'
                        : filterVariant === 'date' ||
                            filterVariant === 'date-range'
                          ? 'date'
                          : 'text';

                    if (
                      filterVariant === 'select' &&
                      column.filterOptions &&
                      !column.filterSearchable
                    ) {
                      return (
                        <div key="filter-input">
                          <label
                            className="block text-xs font-medium mb-2"
                            style={{ color: themeText }}
                          >
                            {t('value') || 'Value'}
                          </label>
                          <select
                            value={localValue}
                            onChange={e => setLocalValue(e.target.value)}
                            className="w-full text-sm rounded-2xl px-3 py-2 border"
                            style={{
                              borderColor: themeBorder,
                              backgroundColor: themeLayer,
                              color: themeText
                            }}
                          >
                            <option value="">{t('all') || 'All'}</option>
                            {column.filterOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }

                    if (filterVariant === 'date-range') {
                      const urlParams = new URLSearchParams(
                        window.location.search
                      );
                      const fromDate = urlParams.get('from') || '';
                      const toDate = urlParams.get('to') || '';

                      const handleDateChange = (
                        type: 'from' | 'to',
                        value: string
                      ) => {
                        const url = new URL(window.location.href);
                        if (value) {
                          url.searchParams.set(type, value);
                        } else {
                          url.searchParams.delete(type);
                        }
                        url.searchParams.set('offset', '0');
                        navigate(url.pathname + url.search, { replace: true });
                      };

                      const handleClearDates = () => {
                        const url = new URL(window.location.href);
                        url.searchParams.delete('from');
                        url.searchParams.delete('to');
                        url.searchParams.set('offset', '0');
                        navigate(url.pathname + url.search, { replace: true });
                      };

                      return (
                        <div key="filter-input" className="space-y-3">
                          <div>
                            <label
                              className="block text-xs font-medium mb-2"
                              style={{ color: themeText }}
                            >
                              {t('from') || 'From'}
                            </label>
                            <input
                              type="date"
                              value={fromDate}
                              onChange={e =>
                                handleDateChange('from', e.target.value)
                              }
                              className="w-full text-sm rounded-2xl px-3 py-2 border"
                              style={{
                                borderColor: themeBorder,
                                backgroundColor: themeLayer,
                                color: themeText
                              }}
                            />
                          </div>
                          <div>
                            <label
                              className="block text-xs font-medium mb-2"
                              style={{ color: themeText }}
                            >
                              {t('to') || 'To'}
                            </label>
                            <input
                              type="date"
                              value={toDate}
                              onChange={e =>
                                handleDateChange('to', e.target.value)
                              }
                              className="w-full text-sm rounded-2xl px-3 py-2 border"
                              style={{
                                borderColor: themeBorder,
                                backgroundColor: themeLayer,
                                color: themeText
                              }}
                            />
                          </div>
                          {(fromDate || toDate) && (
                            <button
                              onClick={handleClearDates}
                              className="w-full px-3 py-2 text-sm rounded-2xl transition-colors"
                              style={{
                                backgroundColor: pick(
                                  'rgb(254 226 226)',
                                  'rgb(127 29 29)'
                                ),
                                color: pick(
                                  'rgb(220 38 38)',
                                  'rgb(252 165 165)'
                                )
                              }}
                            >
                              {t('clear-dates') || 'Clear dates'}
                            </button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div key="filter-input">
                        <label
                          className="block text-xs font-medium mb-2"
                          style={{ color: themeText }}
                        >
                          {t('value') || 'Value'}
                        </label>
                        <input
                          type={inputType}
                          value={localValue}
                          onChange={e => setLocalValue(e.target.value)}
                          placeholder={
                            t('enter-filter-value') || 'Enter value...'
                          }
                          className="w-full text-sm rounded-2xl px-3 py-2 border"
                          style={{
                            borderColor: themeBorder,
                            backgroundColor: themeLayer,
                            color: themeText
                          }}
                          autoFocus
                        />
                      </div>
                    );
                  })()}
              </div>

              <div
                className="flex gap-2 px-4 py-3 border-t"
                style={{ borderColor: themeBorder }}
              >
                {hasActiveFilter && columnFilters.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="flex-1 py-2 px-3 rounded-2xl font-medium text-xs transition-colors"
                    style={{
                      backgroundColor: 'rgb(254 242 242)',
                      color: 'rgb(220 38 38)',
                      border: '1px solid rgb(254 226 226)'
                    }}
                  >
                    {t('clear-all') || 'Clear All'} ({columnFilters.length})
                  </button>
                )}
                {selectedColumn &&
                  columnFilters.find(f => f.id === selectedColumn) && (
                    <button
                      onClick={handleClearSelected}
                      className="flex-1 py-2 px-3 rounded-2xl font-medium text-xs transition-colors"
                      style={{
                        backgroundColor: 'rgb(255 247 237)',
                        color: 'rgb(234 88 12)',
                        border: '1px solid rgb(254 215 170)'
                      }}
                    >
                      {t('clear-selected') || 'Clear'}
                    </button>
                  )}
                <button
                  onClick={handleApply}
                  disabled={!selectedColumn || !localValue}
                  className="flex-1 py-2 px-3 rounded-2xl font-medium text-xs transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: getColour('activeText'),
                    color: 'white'
                  }}
                >
                  {t('apply') || 'Apply'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* -------------------- MobileCardView -------------------- */
interface MobileCardViewProps<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rows: Array<{ id: string; original: T; getVisibleCells: () => Array<any> }>;
  columns: DataGridColumn<T>[];
  onRowClick?: (row: T) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (
    updater: (prev: RowSelectionState) => RowSelectionState
  ) => void;
  renderRowActions?: (row: T) => React.ReactNode;
  actionsProp?: GridAction<T>[];
  execute: (action: GridAction<T>, row: T) => void;
  themeText: string;
  themeLayer: string;
  themeBorder: string;
  enableRowSelection?: boolean;
}

export function MobileCardView<T>({
  rows,
  columns,
  onRowClick,
  rowSelection,
  onRowSelectionChange,
  renderRowActions,
  actionsProp = [],
  execute,
  themeText,
  themeLayer,
  themeBorder,
  enableRowSelection
}: MobileCardViewProps<T>) {
  const { getColour } = useThemeContext();

  const displayColumns = columns.filter(col => {
    const colId = (col.id || col.field || col.accessorKey) as string;
    return colId !== 'select' && colId !== 'expand' && colId !== 'actions';
  });

  const handleToggleSelection = (rowId: string) => {
    onRowSelectionChange((prev: RowSelectionState) => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  return (
    <div className="flex flex-col gap-2 py-2 px-4">
      {rows.map(row => {
        const rowData = row.original as T;
        const rowId = row.id;
        const isSelected = rowSelection[rowId];

        return (
          <div
            key={rowId}
            className="rounded-2xl border transition-all cursor-pointer active:opacity-80"
            style={{
              backgroundColor: themeLayer,
              borderColor: isSelected ? getColour('activeText') : themeBorder,
              borderWidth: isSelected ? '2px' : '1px'
            }}
            onClick={() => onRowClick?.(rowData)}
          >
            <div
              className="flex items-start gap-3 p-2 border-b rounded-t-lg"
              style={{
                borderColor: themeBorder,
                backgroundColor: getColour('firstLayer')
              }}
            >
              {enableRowSelection && (
                <div onClick={e => e.stopPropagation()} className="px-1">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleSelection(rowId)}
                    wrapperClass="!p-0 !m-0"
                    className="!size-3.5"
                  />
                </div>
              )}

              <div className="flex-1 min-w-0">
                {displayColumns[0] && (
                  <div
                    className="font-semibold text-xs mb-0.5 line-clamp-2"
                    style={{ color: themeText }}
                    title={(() => {
                      const colDef = displayColumns[0];
                      const field = colDef.field || colDef.accessorKey;
                      return field && rowData[field as keyof T]
                        ? String(rowData[field as keyof T])
                        : undefined;
                    })()}
                  >
                    {(() => {
                      const colDef = displayColumns[0];
                      const colId = (colDef.id ||
                        colDef.field ||
                        colDef.accessorKey) as string;
                      const cell = row
                        .getVisibleCells()
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .find((c: any) => c.column.id === colId);
                      if (!cell) return null;
                      return flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      );
                    })()}
                  </div>
                )}

                {displayColumns[1] && (
                  <div
                    className="text-xs opacity-70 truncate"
                    style={{ color: themeText }}
                    title={(() => {
                      const colDef = displayColumns[1];
                      const field = colDef.field || colDef.accessorKey;
                      return field && rowData[field as keyof T]
                        ? String(rowData[field as keyof T])
                        : undefined;
                    })()}
                  >
                    {(() => {
                      const colDef = displayColumns[1];
                      const colId = (colDef.id ||
                        colDef.field ||
                        colDef.accessorKey) as string;
                      const cell = row
                        .getVisibleCells()
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .find((c: any) => c.column.id === colId);
                      if (!cell) return null;
                      return flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      );
                    })()}
                  </div>
                )}
              </div>

              {(renderRowActions || actionsProp.length > 0) && (
                <div onClick={e => e.stopPropagation()}>
                  {renderRowActions ? (
                    renderRowActions(rowData)
                  ) : (
                    <RowActionsCell<T>
                      row={rowData}
                      actions={actionsProp}
                      onExecute={(action, r) => execute(action, r)}
                    />
                  )}
                </div>
              )}
            </div>

            {displayColumns.length > 2 && (
              <div className="p-2 space-y-1.5">
                {displayColumns.slice(2).map(colDef => {
                  const colId = (colDef.id ||
                    colDef.field ||
                    colDef.accessorKey) as string;
                  const cell = row
                    .getVisibleCells()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .find((c: any) => c.column.id === colId);
                  if (!cell) return null;

                  const headerText =
                    typeof colDef.header === 'string' ? colDef.header : colId;

                  return (
                    <div
                      key={colId}
                      className="flex items-start gap-2 text-xs h-5"
                    >
                      <dt
                        className="font-medium opacity-70 min-w-[80px] flex-shrink-0"
                        style={{ color: themeText }}
                      >
                        {headerText}:
                      </dt>
                      <dd
                        className="flex-1 flex justify-end items-start"
                        style={{ color: themeText }}
                      >
                        <div className="text-right break-words">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </dd>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
